import { effectiveStack, groupId, hasGroups, hasParentTopology, rankedNodes, stackUnits } from "./canvas-groups";
import { insertElements, type CanvasGraph, type GraphCommand } from "./canvas-commands";
import type { CanvasClipboard } from "./canvas-clipboard";

type Point = { x: number; y: number };
export type CanvasBounds = { left: number; top: number; width: number; height: number };
export type PasteCascade = { key: string; index: number };

export function selectionAnchor(payload: CanvasGraph): Point {
  return payload.nodes.reduce((anchor, node) => ({ x: Math.min(anchor.x, node.position.x),
    y: Math.min(anchor.y, node.position.y) }), { x: Infinity, y: Infinity });
}

/** Screen geometry is plain input; only the page converts its chosen anchor to flow coordinates. */
export function clipboardPlacement(options: {
  bounds: CanvasBounds | null; viewport: Point & { zoom: number }; payloadKey: string; generation: string;
  previous: PasteCascade | null; duplicateAnchor?: Point;
}): { screen: Point; cascade: PasteCascade } | null {
  const { bounds: b, viewport: v } = options;
  if (!b || ![b.left, b.top, b.width, b.height, v.x, v.y, v.zoom].every(Number.isFinite) ||
    b.width <= 0 || b.height <= 0 || v.zoom <= 0) return null;
  const key = JSON.stringify([options.payloadKey, options.generation, b.left, b.top, b.width, b.height, v.x, v.y, v.zoom]);
  const inside = (p: Point) => p.x >= b.left && p.x < b.left + b.width && p.y >= b.top && p.y < b.top + b.height;
  const center = { x: b.left + b.width / 2, y: b.top + b.height / 2 };
  if (options.duplicateAnchor) {
    const offset = { x: b.left + v.x + options.duplicateAnchor.x * v.zoom + 24,
      y: b.top + v.y + options.duplicateAnchor.y * v.zoom + 24 };
    return { screen: inside(offset) ? offset : center, cascade: { key, index: 0 } };
  }
  let index = options.previous?.key === key ? options.previous.index + 1 : 0;
  let screen = { x: center.x + index * 24, y: center.y + index * 24 };
  if (!inside(screen)) { index = 0; screen = center; }
  return { screen, cascade: { key, index } };
}

export function cloneCanvasSelection(payload: CanvasClipboard, destination: CanvasGraph, translation: Point,
  idFactory: (kind: "node" | "edge" | "group") => string): GraphCommand | null {
  if (!Number.isFinite(translation.x) || !Number.isFinite(translation.y)) return null;
  if (hasGroups(payload) && destination.nodes.some(hasParentTopology)) return null;
  // React Flow lifts a child above its parent's computed rank. Reserve enough
  // headroom for the entire legacy hierarchy without rewriting its node order.
  const hierarchy = destination.nodes.some(hasParentTopology) ? destination.nodes.length : 0;
  const maximum = Math.max(-1, ...destination.nodes.map(effectiveStack),
    ...destination.nodes.map(node => Number.isFinite(node.zIndex) ? node.zIndex! : 0)) + hierarchy;
  const start = maximum + 1;
  if (!Number.isFinite(start) || start <= maximum || !Number.isFinite(start + payload.nodes.length) ||
    start + payload.nodes.length <= maximum ||
    payload.nodes.some((_, index) => index > 0 && start + index <= start + (index - 1))) return null;
  const reserved = new Set([...destination.nodes, ...destination.edges].map(item => item.id));
  // Also avoid the source ids when pasting into an otherwise empty board.
  for (const item of [...payload.nodes, ...payload.edges]) reserved.add(item.id);
  for (const node of [...destination.nodes, ...payload.nodes]) { const id = groupId(node); if (id !== undefined) reserved.add(id); }
  const allocate = (kind: "node" | "edge" | "group") => {
    for (let attempt = 0; attempt < 100; attempt++) {
      const id = idFactory(kind);
      if (id && !reserved.has(id)) { reserved.add(id); return id; }
    }
    throw new Error("Could not allocate a fresh identity");
  };
  try {
    const ids = new Map<string, string>(), groups = new Map<string, string>();
    for (const node of payload.nodes) { const id = groupId(node); if (id !== undefined && !groups.has(id)) groups.set(id, allocate("group")); }
    const nodes = rankedNodes(structuredClone(stackUnits(payload.nodes).flat()).map(node => {
      const id = allocate("node"); ids.set(node.id, id);
      const position = { ...node.position, x: node.position.x + translation.x, y: node.position.y + translation.y };
      if (!Number.isFinite(position.x) || !Number.isFinite(position.y)) throw new Error("Invalid translated position");
      return { ...node, id, position, selected: true, ...(groupId(node) !== undefined ? { group_id: groups.get(groupId(node)!)! } : {}) };
    }), start);
    const edges = structuredClone(payload.edges).map(edge => ({ ...edge, id: allocate("edge"),
      source: ids.get(edge.source)!, target: ids.get(edge.target)!, selected: true }));
    const insert = insertElements(nodes, edges);
    return graph => {
      const inserted = insert(graph);
      if (inserted === graph) return graph;
      const newNodes = new Set(nodes.map(node => node.id)), newEdges = new Set(edges.map(edge => edge.id));
      return { nodes: inserted.nodes.map(node => ({ ...node, selected: newNodes.has(node.id) })),
        edges: inserted.edges.map(edge => ({ ...edge, selected: newEdges.has(edge.id) })) };
    };
  } catch { return null; }
}
